/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Stack,
  Button,
  Input,
  Select,
  FormControl,
  FormLabel,
  HStack,
  useToast,
  Switch,
} from '@chakra-ui/react';
import { IoCloseSharp } from 'react-icons/io5';
import ImageComponent from '../../../Components/Image/ImageComponent';
import DynamicButton from '../../../Components/Buttons/DynamicButton';
import {
  addDocumentFirebase,
  updateDocumentFirebase,
} from '../../../Apis/firebaseApi';
import useUserStore from '../../../Hooks/zustand/Store';
import { uploadImage } from '../../../Apis/firebaseFunctions';
import { airbyteOption } from '../../../Utils/dataUtils';

const IntegrationCreate = ({ isOpen, onClose, editData, resSave }) => {
  const toast = useToast();
  const globalState = useUserStore();
  const [isLoading, setIsLoading] = useState(false);
  const dataOptionsType = [
    { value: 'analytics', name: 'ANALYTICS' },
    { value: 'database', name: 'DATABASE' },
    { value: 'chat', name: 'CHAT' },
    { value: 'notification', name: 'NOTIFICATION' },
    { value: 'payment-gateway', name: 'PAYMENT GATEWAY' },
    { value: 'sosial-media', name: 'SOSIAL MEDIA' },
    { value: 'ads', name: 'ADS' },
    { value: 'marketing', name: 'MARKETING' },
  ];

  const [formData, setFormData] = useState({
    image: '',
    title: '',
    connection: '',
    isActivated: true,
    integrationType: 'analytics',
    companies: [],
  });

  const handleSwitchChange = () => {
    setFormData({ ...formData, isActivated: !formData.isActivated });
  };

  useEffect(() => {
    if (editData) {
      setFormData({
        image: editData.image || '',
        title: editData.title || '',
        isActivated: editData.isActivated || '',
        connection: editData.connection || '',
        integrationType: editData.integrationType || 'analytics',
        companies: editData.companies || [],
      });
    } else {
      setFormData({
        image: '',
        title: '',
        connection: '',
        isActivated: true,
        integrationType: 'analytics',
        companies: [],
      });
    }
  }, [editData]);

  const submitImage = async (file) => {
    try {
      const res = (await uploadImage(file[0])).data;

      if (res.status) {
        setFormData((prevData) => ({ ...prevData, image: res.data }));
        toast({
          title: 'Upload Image',
          description: 'Image saved',
          status: 'success',
          duration: 2000,
        });
      }
    } catch (error) {
      toast({
        title: 'Image Size Error',
        description: error.message,
        status: 'error',
      });
    }
  };

  const handleDeleteImage = () => {
    setFormData((prevData) => ({ ...prevData, image: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onSubmit = async () => {
    try {
      if (
        formData.title === '' ||
        formData.integrationType === '' ||
        formData.image === ''
      ) {
        return toast({
          title: 'Input Validation',
          description: 'Please fill in all fields',
          status: 'error',
          position: 'top-right',
          isClosable: true,
        });
      }

      setIsLoading(true);
      globalState.setIsLoading(true);

      if (editData) {
        await updateDocumentFirebase(
          'integration_configs',
          editData.id,
          formData
        );
      } else {
        await addDocumentFirebase(
          'integration_configs',
          formData,
          globalState.currentCompany
        );
      }
      globalState.setIsLoading(false);

      setIsLoading(false);
      setFormData({
        image: '',
        title: '',
        connection: '',
        integrationType: 'analytics',
      });
      resSave();
      onClose();
    } catch (error) {
      globalState.setIsLoading(false);
      console.error('Error submitting data:', error);
      toast({
        title: 'Admin',
        description: 'An error occurred while saving data',
        status: 'error',
      });
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={'3xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Integration</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex w={'100%'} mb={3} gap={5} direction={['column', 'row', 'row']}>
            {/* Box for image upload */}
            <Box
              w={['100%', '50%', '50%']}
              p={5}
              borderRadius="md"
              shadow={'base'}
            >
              <Stack spacing={3} align={'center'}>
                <ImageComponent
                  image={formData.image}
                  name={formData.name}
                  width="200px"
                />

                {formData.image ? (
                  <Button
                    size={'xs'}
                    colorScheme="red"
                    onClick={handleDeleteImage}
                  >
                    Clear Image
                  </Button>
                ) : (
                  <Box>
                    <Input
                      type="file"
                      mb={4}
                      onChange={(e) => submitImage(e.target.files)}
                    />
                  </Box>
                )}
              </Stack>
            </Box>

            <Box p={5} w={['100%', '50%', '50%']} shadow={'base'}>
              <FormControl mb="4">
                <FormLabel>Publish</FormLabel>
                <Switch
                  size="md"
                  onChange={handleSwitchChange}
                  isChecked={formData.isActivated}
                />
              </FormControl>
              <FormControl mb="4">
                <FormLabel>Title</FormLabel>
                <Input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </FormControl>
              <FormControl mb="4">
                <FormLabel>Type</FormLabel>
                <Select
                  value={formData.integrationType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      integrationType: e.target.value,
                    })
                  }
                  required
                >
                  {dataOptionsType.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl mb="4">
                <FormLabel>Connetion</FormLabel>
                <Select
                  value={formData.connection}
                  onChange={(e) =>
                    setFormData({ ...formData, connection: e.target.value })
                  }
                  required
                >
                  <option value="">Choose Connetion</option>
                  {airbyteOption().map((option, index) => (
                    <option key={index} value={option.source_name}>
                      {option.source_name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Flex>

          <HStack
            mt="10"
            mb="5"
            display="flex"
            justifyContent="end"
            spacing={2}
          >
            <DynamicButton
              size={'sm'}
              action="custom"
              color={'red'}
              icon={IoCloseSharp}
              title={'Cancel'}
              onClick={onClose}
            />
            <DynamicButton
              color={'green'}
              size={'sm'}
              action="custom"
              title={'Submit'}
              onClick={onSubmit}
            />
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default IntegrationCreate;
